import { auth } from "../firebase";
import { WorkspaceRole } from "../types";

/**
 * Determine API URL based on environment
 * 
 * This function provides a single source of truth for API URL detection.
 * Always use this function instead of duplicating the logic elsewhere.
 * 
 * Priority:
 * 1. REACT_APP_API_URL environment variable (if set) - Use this for IP address changes during dev
 * 2. Production URL if NODE_ENV is production OR hostname is not localhost/127.0.0.1
 * 3. Development localhost URL
 * 
 * For development with mobile devices (iPhone):
 * - Set REACT_APP_API_URL=http://YOUR_LAPTOP_IP:3001/api in .env.local
 * - Update this when your laptop IP changes
 * - Example: REACT_APP_API_URL=http://192.168.1.100:3001/api
 * 
 * @returns The base API URL (without trailing slash)
 */
export const getApiBaseUrl = (): string => {
  const isDevelopment = process.env.NODE_ENV === "development";
  
  // Use environment variable if set (highest priority)
  // This allows easy IP address changes during development
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // In development mode, automatically detect network IP and use it for API
  // This allows mobile devices to connect without manual IP updates
  if (isDevelopment && typeof window !== "undefined") {
    const hostname = window.location.hostname;
    // If accessing from a network IP (not localhost), use that IP for the API
    const isNetworkIP = hostname.match(/^192\.168\./) || 
                       hostname.match(/^10\./) || 
                       hostname.match(/^172\.(1[6-9]|2[0-9]|3[01])\./);
    
    if (isNetworkIP) {
      // Automatically use the same IP address for the API (no manual updates needed)
      return `http://${hostname}:3001/api`;
    }
    
    // Otherwise use localhost
    return "http://localhost:3001/api";
  }

  // Production mode - use production API URL
  return "https://budget-trace-api-718937684294.us-central1.run.app/api";
};

/**
 * Get the backend health check URL (no auth required).
 * Derived from API base URL: .../api -> .../health
 */
export const getHealthUrl = (): string => {
  const base = getApiBaseUrl();
  const origin = base.replace(/\/api\/?$/, "");
  const healthUrl = `${origin}/health`;
  return healthUrl;
};

/**
 * Check if the dev backend is reachable (health endpoint).
 * Use for dev workspace indicator connection status.
 */
export const checkApiHealth = async (): Promise<boolean> => {
  try {
    const baseUrl = getHealthUrl();
    // Add timestamp to prevent any caching
    const url = `${baseUrl}?t=${Date.now()}`;
    
    if (process.env.NODE_ENV === "development") {
      console.log(`[API Health] Checking: ${url}`);
    }
    
    const controller = new AbortController();
    // Reduce timeout to 3 seconds for faster failure detection
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    
    const res = await fetch(url, { 
      method: "GET", 
      signal: controller.signal,
      // Aggressive cache prevention (using cache option and timestamp query param, no custom headers to avoid CORS issues)
      cache: "no-store",
      // Don't add custom headers - they cause CORS preflight issues
      // The timestamp query parameter (?t=...) and cache: "no-store" are sufficient
    });
    
    clearTimeout(timeoutId);
    
    if (process.env.NODE_ENV === "development") {
      console.log(`[API Health] Response status: ${res.status} ${res.statusText}`);
    }
    
    if (!res.ok) {
      if (process.env.NODE_ENV === "development") {
        console.warn(`[API Health] Backend returned ${res.status} ${res.statusText}`);
      }
      return false;
    }
    
    // Verify we got a valid JSON response
    try {
      const data = await res.json();
      if (process.env.NODE_ENV === "development") {
        console.log(`[API Health] Response data:`, data);
      }
      if (data.status === "ok") {
        if (process.env.NODE_ENV === "development") {
          console.log(`[API Health] ✅ Health check passed`);
        }
        return true;
      } else {
        if (process.env.NODE_ENV === "development") {
          console.warn(`[API Health] Backend returned invalid status:`, data);
        }
        return false;
      }
    } catch (parseError) {
      // If JSON parsing fails, still consider it a failure
      if (process.env.NODE_ENV === "development") {
        console.warn(`[API Health] Failed to parse JSON response:`, parseError);
      }
      return false;
    }
  } catch (error) {
    // Log error in development for debugging
    if (process.env.NODE_ENV === "development") {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorStack = error instanceof Error ? error.stack : undefined;
      
      if (errorMessage.includes("aborted") || errorMessage.includes("timeout")) {
        console.warn(`[API Health] Request timed out - backend may be down - URL: ${getHealthUrl()}`);
      } else if (errorMessage.includes("Failed to fetch") || 
                 errorMessage.includes("NetworkError") ||
                 errorMessage.includes("Network request failed") ||
                 errorMessage.includes("ERR_CONNECTION_REFUSED") ||
                 errorMessage.includes("ERR_EMPTY_RESPONSE") ||
                 errorMessage.includes("Load failed")) {
        console.warn(`[API Health] Backend server is not reachable (connection refused) - URL: ${getHealthUrl()}`);
      } else {
        console.warn(`[API Health] Connection check failed:`, errorMessage, errorStack);
      }
    }
    return false;
  }
};

/**
 * Get Firebase ID token for API authentication
 * 
 * Exported for use in components that need to make authenticated requests
 * outside of the api service (e.g., for special cases like transactions fetch)
 */
export const getAuthToken = async (): Promise<string | null> => {
  const user = auth.currentUser;
  if (!user) return null;
  return await user.getIdToken();
};

/**
 * Make an authenticated API request
 */
const apiRequest = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error("User not authenticated");
  }

  // Get API URL dynamically to ensure we always use the current environment
  const apiBaseUrl = getApiBaseUrl();
  const url = `${apiBaseUrl}${endpoint}`;
  
  // Log the request for debugging (only in development)
  // Removed frequent request logging to keep console clean
  
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...options.headers,
  };

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });
    return response;
  } catch (error) {
    // Handle network errors
    if (error instanceof TypeError && error.message === "Failed to fetch") {
      // Provide more helpful error message based on API URL being used
      if (apiBaseUrl.includes("localhost")) {
        console.error(`[API] Connection failed to localhost backend. Make sure the backend is running:`);
        console.error(`[API]   cd backend && npm run dev`);
        throw new Error(
          "Cannot connect to the backend server. Please make sure the backend server is running on port 3001. Start it with: cd backend && npm run dev"
        );
      } else {
        console.error(`[API] Connection failed to production backend: ${apiBaseUrl}`);
        throw new Error(
          "Cannot connect to the backend server. The API server may be temporarily unavailable. Please try again later or contact support."
        );
      }
    }
    throw error;
  }
};

/**
 * API client functions
 */
export const api = {
  /**
   * Send workspace invitation
   */
  sendInvitation: async (workspaceId: string, email: string, role: WorkspaceRole = "user"): Promise<{ id: string; email: string; expiresAt: string; emailSent?: boolean; emailError?: { type: string; message: string } }> => {
    const response = await apiRequest(`/workspaces/${workspaceId}/invitations`, {
      method: "POST",
      body: JSON.stringify({ email, role }),
    });

    if (!response.ok) {
      let errorMessage = "Failed to send invitation";
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch {
        // If response is not JSON, use status text
        errorMessage = response.statusText || `Server returned ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  },

  /**
   * Resend workspace invitation
   */
  resendInvitation: async (workspaceId: string, invitationId: string): Promise<{ id: string; email: string; expiresAt: string; emailSent?: boolean; emailError?: { type: string; message: string } }> => {
    const response = await apiRequest(`/workspaces/${workspaceId}/invitations/${invitationId}/resend`, {
      method: "POST",
    });

    if (!response.ok) {
      let errorMessage = "Failed to resend invitation";
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch {
        // If response is not JSON, use status text
        errorMessage = response.statusText || `Server returned ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  },

  /**
   * Create a new workspace
   */
  createWorkspace: async (name: string, defaultCurrency: string): Promise<{ id: string; name: string; ownerId: string }> => {
    const response = await apiRequest("/workspaces", {
      method: "POST",
      body: JSON.stringify({ name, defaultCurrency }),
    });

    if (!response.ok) {
      let errorMessage = "Failed to create workspace";
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch {
        errorMessage = response.statusText || `Server returned ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  },

  /**
   * Get all workspaces for the authenticated user
   */
  getWorkspaces: async (): Promise<Array<{ id: string; name: string; ownerId: string; createdAt: any; logoUrl?: string }>> => {
    try {
      const controller = new AbortController();
      // Reduced timeout in production - if API is slow, fail fast so UI can use cached data
      const apiBaseUrl = getApiBaseUrl();
      const timeoutMs = apiBaseUrl.includes("localhost") ? 10000 : 8000; // 8 seconds in production, 10 seconds locally
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      
      const token = await getAuthToken();
      if (!token) {
        throw new Error("User not authenticated");
      }

      const url = `${apiBaseUrl}/workspaces`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorMessage = "Failed to fetch workspaces";
        try {
          const error = await response.json();
          errorMessage = error.error || errorMessage;
        } catch {
          errorMessage = response.statusText || `Server returned ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      return response.json();
      } catch (error: any) {
      if (error.name === 'AbortError') {
        const apiBaseUrl = getApiBaseUrl();
        const timeoutMs = apiBaseUrl.includes("localhost") ? 10000 : 8000;
        console.error(`[API] getWorkspaces request timeout after ${timeoutMs}ms`);
        // Don't throw error - allow app to continue with cached/empty data
        // This prevents blocking initial load when API is slow
        console.warn("[API] Returning empty array due to timeout - app will continue with cached data");
        return [];
      }
      throw error;
    }
  },

  /**
   * Get all workspaces (admin only). Returns 403 if current user is not in admin allowlist.
   * Returns { active, deleted }. Backward compatible: if response is an array, treated as active only.
   */
  getAdminWorkspaces: async (): Promise<{
    active: Array<{
      id: string;
      name: string;
      ownerId: string;
      ownerEmail?: string;
      type?: string;
      memberCount: number;
      transactionCount: number;
      firebaseReads?: number;
      firebaseWrites?: number;
      firebaseCostUsd?: number;
      storageBytesUsed?: number;
    }>;
    deleted: Array<{
      id: string;
      name: string;
      ownerId: string;
      ownerEmail?: string;
      type?: string;
      memberCount: number;
      transactionCount: number;
      deletedAt: string;
    }>;
  }> => {
    const response = await apiRequest("/admin/workspaces", { method: "GET" });
    if (!response.ok) {
      if (response.status === 403) {
        throw new Error("FORBIDDEN");
      }
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || "Failed to fetch admin workspaces");
    }
    const data = await response.json();
    if (Array.isArray(data)) {
      return { active: data, deleted: [] };
    }
    return { active: data.active ?? [], deleted: data.deleted ?? [] };
  },

  /**
   * Sync admin workspace list: pass current active workspace IDs; backend adds any that no longer exist in Firebase
   * to the deleted list, then returns { active, deleted }. Admin only.
   */
  syncAdminWorkspaces: async (previousActiveIds: string[]): Promise<{
    active: Array<{
      id: string;
      name: string;
      ownerId: string;
      ownerEmail?: string;
      type?: string;
      memberCount: number;
      transactionCount: number;
      firebaseReads?: number;
      firebaseWrites?: number;
      firebaseCostUsd?: number;
      storageBytesUsed?: number;
    }>;
    deleted: Array<{
      id: string;
      name: string;
      ownerId: string;
      ownerEmail?: string;
      type?: string;
      memberCount: number;
      transactionCount: number;
      deletedAt: string;
    }>;
  }> => {
    const response = await apiRequest("/admin/workspaces/sync", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ previousActiveIds }),
    });
    if (!response.ok) {
      if (response.status === 403) throw new Error("FORBIDDEN");
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || "Failed to sync admin workspaces");
    }
    const data = await response.json();
    return { active: data.active ?? [], deleted: data.deleted ?? [] };
  },

  /**
   * Get aggregated usage for admin dashboard (interval, total reads/writes, cost USD).
   * Admin only.
   */
  getAdminUsage: async (interval?: string): Promise<{
    interval: { label: string; start: string; end: string };
    totalReads: number;
    totalWrites: number;
    estimatedCostUsd: number;
    firebasePricing: { readsPer100k: number; writesPer100k: number };
    actualCostUsd: number | null;
    actualCostBreakdown?: Array<{ serviceId: string; serviceDescription: string; cost: number }>;
  }> => {
    const params = interval ? `?interval=${encodeURIComponent(interval)}` : "";
    const response = await apiRequest(`/admin/usage${params}`, { method: "GET" });
    if (!response.ok) {
      if (response.status === 403) throw new Error("FORBIDDEN");
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || "Failed to fetch admin usage");
    }
    return response.json();
  },

  /**
   * Set active workspace for user
   */
  setActiveWorkspace: async (workspaceId: string): Promise<{ success: boolean; workspaceId: string }> => {
    const response = await apiRequest(`/workspaces/${workspaceId}/active`, {
      method: "PUT",
    });

    if (!response.ok) {
      let errorMessage = "Failed to switch workspace";
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch {
        errorMessage = response.statusText || `Server returned ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  },

  /**
   * Leave a workspace (user removes themselves, preserves all data)
   */
  leaveWorkspace: async (workspaceId: string): Promise<{ success: boolean; message?: string }> => {
    const response = await apiRequest(`/workspaces/${workspaceId}/leave`, {
      method: "DELETE",
    });

    if (!response.ok) {
      let errorMessage = "Failed to leave workspace";
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch {
        errorMessage = response.statusText || `Server returned ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  },

  /**
   * Delete a workspace (owner only)
   */
  deleteWorkspace: async (workspaceId: string): Promise<{ success: boolean }> => {
    const response = await apiRequest(`/workspaces/${workspaceId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      let errorMessage = "Failed to delete workspace";
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch {
        errorMessage = response.statusText || `Server returned ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  },

  /**
   * Get invitation by ID (public endpoint - doesn't require authentication)
   */
  getInvitation: async (invitationId: string): Promise<{
    id: string;
    email: string;
    workspaceId: string;
    workspaceName: string;
    invitedBy: string;
    status: "pending" | "accepted" | "expired" | "declined";
    role?: WorkspaceRole;
    createdAt: any;
    expiresAt: any;
  }> => {
    const apiBaseUrl = getApiBaseUrl();
    const url = `${apiBaseUrl}/invitations/public/${invitationId}`;
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          let errorMessage = "Failed to fetch invitation";
          try {
            const error = await response.json();
            errorMessage = error.error || errorMessage;
            console.error(`❌ [API] Error response:`, error);
          } catch {
            errorMessage = response.statusText || `Server returned ${response.status}`;
            console.error(`❌ [API] Error status: ${response.status} - ${errorMessage}`);
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();
        return data;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.error(`⏱️ [API] Request timeout after 30 seconds`);
        throw new Error("Request timeout: The server took too long to respond. Please try again.");
      } else if (error.message) {
        console.error(`❌ [API] Fetch error:`, error.message);
        throw error;
      } else {
        console.error(`❌ [API] Unknown error:`, error);
        throw new Error("Network error: Could not connect to the server. Please check your internet connection.");
      }
    }
  },

  /**
   * Get pending invitations for a user by email (public endpoint)
   */
  getUserInvitations: async (email: string): Promise<Array<{
    id: string;
    email: string;
    workspaceId: string;
    workspaceName: string;
    invitedBy: string;
    status: "pending" | "accepted" | "expired" | "declined";
    role?: WorkspaceRole;
    createdAt: any;
    expiresAt: any;
  }>> => {
    const apiBaseUrl = getApiBaseUrl();
    const response = await fetch(`${apiBaseUrl}/invitations/user/${encodeURIComponent(email)}`);

    if (!response.ok) {
      let errorMessage = "Failed to fetch invitations";
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch {
        errorMessage = response.statusText || `Server returned ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  },

  /**
   * Get all categories in a workspace (optionally filtered by type)
   */
  getCategories: async (workspaceId: string, type?: "expense" | "income"): Promise<Array<{
    id: string;
    name: string;
    type: "expense" | "income";
    groupId: string;
    details?: Array<{
      name: string;
      type: "list" | "measurable";
      unit?: string;
      listItems?: string[];
    }>;
    enabledDetails?: string[];
    defaultValues?: Record<string, string | number>;
    createdAt: any;
    updatedAt?: any;
    order?: number;
  }>> => {
    const queryParam = type ? `?type=${type}` : "";
    const response = await apiRequest(`/workspaces/${workspaceId}/categories${queryParam}`, {
      method: "GET",
    });

    if (!response.ok) {
      let errorMessage = "Failed to fetch categories";
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch {
        errorMessage = response.statusText || `Server returned ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  },

  /**
   * Create a new category
   */
  createCategory: async (workspaceId: string, name: string, type: "expense" | "income", groupId: string, details?: Array<{
    name: string;
    type: "list" | "measurable";
    unit?: string;
    listItems?: string[];
  }>): Promise<{
    id: string;
    name: string;
    type: "expense" | "income";
    groupId: string;
    details?: Array<{
      name: string;
      type: "list" | "measurable";
      unit?: string;
      listItems?: string[];
    }>;
    createdAt: any;
  }> => {
    const response = await apiRequest(`/workspaces/${workspaceId}/categories`, {
      method: "POST",
      body: JSON.stringify({ name, type, groupId, details }),
    });

    if (!response.ok) {
      let errorMessage = "Failed to create category";
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch {
        errorMessage = response.statusText || `Server returned ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  },

  /**
   * Update a category
   */
  updateCategory: async (
    workspaceId: string,
    categoryId: string,
    name?: string,
    groupId?: string,
    details?: Array<{ // Legacy support - deprecated, use enabledDetails/defaultValues instead
      name: string;
      type: "list" | "measurable";
      unit?: string;
      listItems?: string[];
    }>,
    order?: number,
    enabledDetails?: string[],
    defaultValues?: Record<string, string | number>
  ): Promise<{
    id: string;
    name: string;
    type: "expense" | "income";
    groupId: string;
    enabledDetails?: string[];
    defaultValues?: Record<string, string | number>;
    createdAt: any;
    updatedAt?: any;
    order?: number;
  }> => {
    const response = await apiRequest(`/workspaces/${workspaceId}/categories/${categoryId}`, {
      method: "PUT",
      body: JSON.stringify({ name, groupId, details, order, enabledDetails, defaultValues }),
    });

    if (!response.ok) {
      let errorMessage = "Failed to update category";
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch {
        errorMessage = response.statusText || `Server returned ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  },

  /**
   * Delete a category (only if unused)
   */
  deleteCategory: async (workspaceId: string, categoryId: string): Promise<void> => {
    const response = await apiRequest(`/workspaces/${workspaceId}/categories/${categoryId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      let errorMessage = "Failed to delete category";
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch {
        errorMessage = response.statusText || `Server returned ${response.status}`;
      }
      throw new Error(errorMessage);
    }
  },

  /**
   * Get all groups in a workspace (optionally filtered by type)
   */
  getGroups: async (workspaceId: string, type?: "expense" | "income"): Promise<Array<{
    id: string;
    name: string;
    type: "expense" | "income";
    details?: import("../types").TransactionCategoryDetail[];
    createdAt: any;
    updatedAt?: any;
  }>> => {
    const queryParam = type ? `?type=${type}` : "";
    const response = await apiRequest(`/workspaces/${workspaceId}/categoryGroups${queryParam}`, {
      method: "GET",
    });

    if (!response.ok) {
      let errorMessage = "Failed to fetch groups";
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch {
        errorMessage = response.statusText || `Server returned ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    const groups = await response.json();
    
    // Ensure details is always an array (even if undefined or null from API)
    const normalizedGroups = groups.map((group: any) => ({
      ...group,
      details: Array.isArray(group.details) ? group.details : [],
    }));
    
    // Log for debugging
    normalizedGroups.forEach((group: any) => {
      if (group.details && group.details.length > 0) {
        // Removed frequent logging to keep console clean
      }
    });
    
    return normalizedGroups;
  },

  /**
   * Create a new group
   */
  createGroup: async (workspaceId: string, name: string, type: "expense" | "income"): Promise<{
    id: string;
    name: string;
    type: "expense" | "income";
    createdAt: any;
  }> => {
    const response = await apiRequest(`/workspaces/${workspaceId}/categoryGroups`, {
      method: "POST",
      body: JSON.stringify({ name, type }),
    });

    if (!response.ok) {
      let errorMessage = "Failed to create group";
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch {
        errorMessage = response.statusText || `Server returned ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  },

  /**
   * Update a group
   */
  updateGroup: async (workspaceId: string, groupId: string, name?: string, description?: string, details?: import("../types").TransactionCategoryDetail[], order?: number): Promise<{
    id: string;
    name: string;
    type: "expense" | "income";
    description?: string;
    details?: import("../types").TransactionCategoryDetail[];
    order?: number;
    createdAt: any;
    updatedAt?: any;
  }> => {
    const body: { name?: string; description?: string; details?: import("../types").TransactionCategoryDetail[]; order?: number } = {};
    
    // Only include name if it's defined, not null, and not empty (treat empty string as undefined)
    // Be extra defensive: check for all falsy values and ensure it's a non-empty string
    if (name !== undefined && name !== null && typeof name === "string") {
      const trimmedName = name.trim();
      // Only include if it's a non-empty string after trimming
      if (trimmedName !== "" && trimmedName !== "null" && trimmedName !== "undefined") {
        body.name = trimmedName;
      }
    }
    
    // Only include description if it's defined, not null, and not empty (treat empty string as undefined)
    if (description !== undefined && description !== null && typeof description === "string") {
      const trimmedDescription = description.trim();
      // Only include if it's a non-empty string after trimming
      if (trimmedDescription !== "" && trimmedDescription !== "null" && trimmedDescription !== "undefined") {
        body.description = trimmedDescription;
      }
    }
    
    // Only include details if it's defined and is an array (empty array is valid)
    if (details !== undefined && details !== null) {
      if (Array.isArray(details)) {
        body.details = details;
      } else {
        // If details is provided but not an array, don't include it (let backend handle validation)
        console.warn("updateGroup: details provided but not an array, omitting from request");
      }
    }
    
    if (order !== undefined && order !== null && typeof order === "number") {
      body.order = order;
    }
    
    // Removed frequent request logging to keep console clean
    
    const response = await apiRequest(`/workspaces/${workspaceId}/categoryGroups/${groupId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      let errorMessage = "Failed to update group";
      let errorDetails: any = null;
      try {
        errorDetails = await response.json();
        errorMessage = errorDetails.error || errorMessage;
        console.error(`[updateGroup] Error response:`, {
          status: response.status,
          statusText: response.statusText,
          error: errorDetails,
        });
        // Log the full error object in a way that's easy to read
        console.error(`[updateGroup] Full error details:`, JSON.stringify(errorDetails, null, 2));
        console.error(`[updateGroup] Error message:`, errorMessage);
      } catch (e) {
        errorMessage = response.statusText || `Server returned ${response.status}`;
        console.error(`[updateGroup] Error parsing response:`, e);
      }
      throw new Error(errorMessage);
    }

    return response.json();
  },

  /**
   * Delete a group (only if unused)
   */
  deleteGroup: async (workspaceId: string, groupId: string): Promise<void> => {
    const response = await apiRequest(`/workspaces/${workspaceId}/categoryGroups/${groupId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      let errorMessage = "Failed to delete group";
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch {
        errorMessage = response.statusText || `Server returned ${response.status}`;
      }
      throw new Error(errorMessage);
    }
  },

  /**
   * Helper function to make an authenticated fetch request
   * 
   * Use this when you need to make a direct API call that's not covered by the api service.
   * This ensures consistent authentication and error handling.
   * 
   * @param endpoint - API endpoint (e.g., "/workspaces/123/transactions")
   * @param options - Fetch options (method, body, etc.)
   * @returns Promise<Response>
   * 
   * @example
   * ```typescript
   * const response = await api.makeAuthenticatedRequest(
   *   `/workspaces/${workspaceId}/transactions?type=expense`,
   *   { method: "GET" }
   * );
   * ```
   */
  makeAuthenticatedRequest: async (
    endpoint: string,
    options: RequestInit = {}
  ): Promise<Response> => {
    const token = await getAuthToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const url = `${getApiBaseUrl()}${endpoint}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });
      return response;
    } catch (error) {
      // Handle network errors
      if (error instanceof TypeError && error.message === "Failed to fetch") {
        const apiBaseUrl = getApiBaseUrl();
        if (apiBaseUrl.includes("localhost")) {
          throw new Error(
            "Cannot connect to the backend server. Please make sure the backend server is running on port 3001. Start it with: cd backend && npm run dev"
          );
        } else {
          throw new Error(
            "Cannot connect to the backend server. The API server may be temporarily unavailable. Please try again later or contact support."
          );
        }
      }
      throw error;
    }
  },

  /**
   * Delete a transaction (reverses account balance changes)
   */
  deleteTransaction: async (workspaceId: string, transactionId: string): Promise<{ success: boolean }> => {
    const response = await apiRequest(`/workspaces/${workspaceId}/transactions/${transactionId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      let errorMessage = "Failed to delete transaction";
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch {
        errorMessage = response.statusText || `Server returned ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  },

  /**
   * Check if the backend server is reachable
   * This is useful for diagnostics and connection testing
   */
  checkHealth: async (): Promise<{ status: string; message: string; apiUrl: string }> => {
    const apiBaseUrl = getApiBaseUrl();
    try {
      const response = await fetch(`${apiBaseUrl.replace('/api', '')}/health`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        return {
          status: "ok",
          message: data.message || "Backend server is reachable",
          apiUrl: apiBaseUrl,
        };
      } else {
        return {
          status: "error",
          message: `Backend server returned status ${response.status}`,
          apiUrl: apiBaseUrl,
        };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return {
        status: "error",
        message: `Cannot connect to backend: ${errorMessage}`,
        apiUrl: apiBaseUrl,
      };
    }
  },

  /**
   * Get a short-lived signed URL for an attachment (auth + workspace check).
   * Use this instead of opening the raw Firebase Storage URL.
   */
  getAttachmentSignedUrl: async (
    workspaceId: string,
    attachmentUrl: string
  ): Promise<{ url: string; expiresIn: number }> => {
    const response = await apiRequest(
      `/workspaces/${workspaceId}/attachments/signed-url`,
      {
        method: "POST",
        body: JSON.stringify({ url: attachmentUrl }),
      }
    );

    if (!response.ok) {
      let errorMessage = "Failed to get attachment link";
      try {
        const err = await response.json();
        errorMessage = err.message || err.error || errorMessage;
      } catch {
        errorMessage = response.statusText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  },
};

